import React from "react";

type CarouselApi = {
  scrollNext: () => void;
  scrollPrev: () => void;
  scrollTo: (index: number) => void;
  selectedScrollSnap: () => number;
  scrollSnaps: () => number[];
  on: (event: "select", cb: () => void) => void;
};

type CarouselContextValue = {
  viewportRef: React.RefObject<HTMLDivElement>;
  api: CarouselApi;
  slideCount: number;
  setSlideCount: (n: number) => void;
};

const CarouselContext = React.createContext<CarouselContextValue | null>(null);

function useCarouselCtx() {
  const ctx = React.useContext(CarouselContext);
  if (!ctx) throw new Error("Carousel components must be used within <Carousel />");
  return ctx;
}

export type { CarouselApi };

export function Carousel({
  className,
  setApi,
  opts,
  children,
}: React.PropsWithChildren<{
  className?: string;
  setApi?: (api: CarouselApi) => void;
  // Compatibility prop used by previous UI lib; ignored in this lightweight implementation.
  opts?: { align?: string; loop?: boolean } | undefined;
}>) {
  void opts;
  const viewportRef = React.useRef<HTMLDivElement>(null as unknown as HTMLDivElement);
  const selectListeners = React.useRef(new Set<() => void>());
  const [slideCount, setSlideCount] = React.useState(0);

  const api = React.useMemo<CarouselApi>(() => {
    return {
      scrollNext: () => {
        const el = viewportRef.current;
        if (!el) return;
        el.scrollBy({ left: el.clientWidth, behavior: "smooth" });
      },
      scrollPrev: () => {
        const el = viewportRef.current;
        if (!el) return;
        el.scrollBy({ left: -el.clientWidth, behavior: "smooth" });
      },
      scrollTo: (index: number) => {
        const el = viewportRef.current;
        if (!el) return;
        const width = el.clientWidth || 0;
        el.scrollTo({ left: Math.max(0, index) * width, behavior: "smooth" });
      },
      selectedScrollSnap: () => {
        const el = viewportRef.current;
        if (!el || el.clientWidth === 0) return 0;
        return Math.round(el.scrollLeft / el.clientWidth);
      },
      scrollSnaps: () => Array.from({ length: slideCount }, (_, i) => i),
      on: (_event, cb) => {
        selectListeners.current.add(cb);
      },
    };
  }, [slideCount]);

  React.useEffect(() => {
    setApi?.(api);
  }, [api, setApi]);

  React.useEffect(() => {
    const el = viewportRef.current;
    if (!el) return;
    const onScroll = () => {
      for (const cb of selectListeners.current) cb();
    };
    el.addEventListener("scroll", onScroll, { passive: true });
    return () => el.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <CarouselContext.Provider value={{ viewportRef, api, slideCount, setSlideCount }}>
      <div className={className}>{children}</div>
    </CarouselContext.Provider>
  );
}

export function CarouselContent({
  className,
  opts,
  children,
}: React.PropsWithChildren<{
  className?: string;
  // Compatibility prop used by previous UI lib; ignored in this lightweight implementation.
  opts?: { align?: string; loop?: boolean } | undefined;
}>) {
  void opts;
  const { viewportRef, setSlideCount } = useCarouselCtx();
  const slides = React.Children.toArray(children).filter(Boolean);

  React.useEffect(() => {
    setSlideCount(slides.length);
  }, [slides.length, setSlideCount]);

  return (
    <div
      ref={viewportRef}
      className={[
        "flex w-full snap-x snap-mandatory overflow-x-auto scroll-smooth",
        "[-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {children}
    </div>
  );
}

export function CarouselItem({
  className,
  children,
}: React.PropsWithChildren<{ className?: string }>) {
  return (
    <div className={["w-full shrink-0 snap-start", className].filter(Boolean).join(" ")}>
      {children}
    </div>
  );
}

export function CarouselPrevious({
  className,
  children,
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { api } = useCarouselCtx();
  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        api.scrollPrev();
        (children as any)?.props?.onClick?.(e);
      }}
    >
      {children ?? "Prev"}
    </button>
  );
}

export function CarouselNext({
  className,
  children,
}: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  const { api } = useCarouselCtx();
  return (
    <button
      type="button"
      className={className}
      onClick={(e) => {
        api.scrollNext();
        (children as any)?.props?.onClick?.(e);
      }}
    >
      {children ?? "Next"}
    </button>
  );
}


